/**
 * Synchronous copy using a hidden textarea + execCommand.
 * Must run in the same synchronous turn as the user gesture (click).
 * Kept as fallback when the Clipboard API is unavailable or denied.
 */
export function copyTextViaExecCommand(text: string): boolean {
  if (typeof document === "undefined" || text === "") return false;
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.readOnly = true;
  ta.setAttribute("aria-hidden", "true");
  // iOS/Safari often require the field to be “visible” (not fully off-screen).
  ta.style.position = "fixed";
  ta.style.top = "0";
  ta.style.left = "0";
  ta.style.width = "1px";
  ta.style.height = "1px";
  ta.style.padding = "0";
  ta.style.border = "none";
  ta.style.outline = "none";
  ta.style.opacity = "0";
  ta.style.zIndex = "-1";
  document.body.appendChild(ta);
  ta.focus({ preventScroll: true });
  ta.select();
  ta.setSelectionRange(0, text.length);
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(ta);
  }
}

/**
 * Copy plain text. Call from a **direct** user gesture (e.g. `async` `onClick` with `await`).
 *
 * Important: Do **not** call `execCommand` first and then `await writeText` from a non-async
 * handler — the gesture expires before `writeText` runs, so the clipboard may not update
 * even when the promise resolves.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  if (text === "") return false;

  const canUseClipboardApi =
    typeof navigator !== "undefined" &&
    typeof navigator.clipboard?.writeText === "function" &&
    typeof window !== "undefined" &&
    window.isSecureContext;

  if (canUseClipboardApi) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission denied or unsupported — fall through to execCommand
    }
  }

  return copyTextViaExecCommand(text);
}

